import React, { useState } from 'react'
import { Sparkles, Loader2, Search, Zap, Crosshair } from 'lucide-react'
import type { Nurse } from '../lib/types'

interface AiSmartMatcherProps {
  nurses: Nurse[]
  onSelectNurse: (n: Nurse) => void
}

interface MatchResult {
  nurseId: string
  score: number
  reason: string
}

export default function AiSmartMatcher({ nurses, onSelectNurse }: AiSmartMatcherProps) {
  const [query, setQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<MatchResult[]>([])
  const [error, setError] = useState('')

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!query.trim() || isSearching) return

    const apiKey = localStorage.getItem('GEMINI_API_KEY')
    if (!apiKey) {
      setError('Chiave API Gemini mancante. Inseriscila nelle Impostazioni.')
      return
    }

    setIsSearching(true)
    setError('')
    setResults([])

    try {
      // Prepare a lean version of nurses database to fit the LLM context limits
      const leanNurses = nurses.map(n => ({
        id: n.id,
        nome: n.nome + ' ' + n.cognome.charAt(0) + '.', // anonimize slightly to save token but keep it readable
        qualifica: n.qualifica,
        regione: n.regione,
        provincia: n.provincia,
        contratto: n.tipo_contratto,
        disponibilita: n.disponibilita,
        note: n.note?.substring(0, 50),
        competenze: n.tags
      }))

      const systemPrompt = `Sei "QuiCare Smart Matcher", l'A.I. avanzata di matching HR. Ho una richiesta specifica e una lista di operatori sanitari (JSON). 
Devi trovare i MIGLIORI 3 professionisti per la richiesta, analizzando geograficamente (se viene menzionata una città), competenze e contratto.
Restituisci ESATTAMENTE e SOLO un array JSON in questo preciso formato: 
[{"nurseId": "string-uuid-esatto-dal-json", "score": 95, "reason": "Motivo conciso della scelta max 15 parole."}]

Richiesta dell'utente: "${query}"
Database Operatori: ${JSON.stringify(leanNurses)}`

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ role: 'user', parts: [{ text: systemPrompt }] }] })
      })

      if (!response.ok) { let eMsg = `Network error (${response.status})`; try { const eData = await response.json(); eMsg += `: ${eData.error?.message}`; } catch(e){} throw new Error(eMsg); }
      
      const data = await response.json()
      let rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || '[]'
      rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim()
      
      const parsed: MatchResult[] = JSON.parse(rawText)
      setResults(parsed)
      
      if (parsed.length === 0) setError("Nessun match trovato per i criteri inseriti.")

    } catch (err: any) {
      console.error(err)
      setError("Errore durante l'elaborazione A.I. Riprova con una query diversa.")
    } finally {
      setIsSearching(false)
    }
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-200 p-5 shadow-inner relative overflow-hidden mt-4 animate-fade-in">
      <div className="absolute top-0 right-0 p-8 opacity-5">
        <Sparkles className="w-32 h-32 text-indigo-500" />
      </div>

      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-white">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-black text-slate-800">A.I. Smart Matcher</h3>
            <p className="text-xs text-slate-500">Usa il linguaggio naturale per trovare i candidati perfetti in tutto il database.</p>
          </div>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-400" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder='Es: "Mi serve un infermiere urgente con partita IVA a Milano nord bravo nei prelievi"'
              className="w-full pl-10 pr-3 py-3 rounded-xl border border-indigo-100 bg-white/70 backdrop-blur-md text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all font-medium text-slate-700 placeholder-indigo-300"
              disabled={isSearching}
            />
          </div>
          <button
            type="submit"
            disabled={!query.trim() || isSearching}
            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl shadow-md cursor-pointer hover:bg-indigo-700 disabled:opacity-50 transition-all text-sm"
          >
            {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {isSearching ? 'Analisi in corso...' : 'Trova Match'}
          </button>
        </form>

        {error && <div className="mt-4 text-xs font-bold text-red-500 bg-red-50 p-3 rounded-lg border border-red-100">{error}</div>}

        {results.length > 0 && (
          <div className="mt-6 space-y-3">
            <div className="text-xs font-black uppercase text-indigo-800 tracking-wider flex items-center gap-2 mb-3">
              <Crosshair className="w-4 h-4" /> I Migliori Match (Top {results.length})
            </div>
            {results.map((res, idx) => {
              const nurse = nurses.find(n => n.id === res.nurseId)
              if (!nurse) return null

              return (
                <div 
                  key={res.nurseId} 
                  onClick={() => onSelectNurse(nurse)}
                  className="bg-white border border-indigo-100 rounded-xl p-4 flex flex-wrap sm:flex-nowrap items-center justify-between gap-4 cursor-pointer hover:shadow-lg hover:border-indigo-300 transition-all group"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-white shrink-0 shadow-inner ${
                      idx === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500' :
                      idx === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400' :
                      'bg-gradient-to-br from-amber-600 to-amber-700'
                    }`}>
                      #{idx + 1}
                    </div>
                    <div>
                      <div className="font-bold text-slate-800 text-sm group-hover:text-indigo-600 transition-colors">
                        {nurse.cognome} {nurse.nome}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] font-bold text-slate-600">{nurse.qualifica}</span>
                        <span className="bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded text-[10px] font-bold">{nurse.provincia}</span>
                        <span className="opacity-60">{nurse.tipo_contratto}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="w-full sm:w-auto bg-indigo-50/50 p-2.5 rounded-lg border border-indigo-50/80 flex-1 max-w-[300px]">
                    <div className="text-[10px] uppercase font-black tracking-widest text-indigo-400 mb-1 flex items-center justify-between">
                      Motivo Scelta <span className="text-emerald-500 font-bold bg-emerald-50 px-1.5 rounded">{res.score}% Match</span>
                    </div>
                    <p className="text-xs text-slate-600 font-medium leading-relaxed italic">"{res.reason}"</p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
